import type { Cosmetics, SkinAccess } from "@highmarch/app";
import type { SkinKind } from "@highmarch/domain";
import { SKIN_LOOKS, STOCK_LOOKS, SkinLook, lookOf } from "../../render/skins/looks";
import { uiUrl } from "../portraits";
import { PET_KITS } from "../../render/skins/kits";
import { petPortraitUrl } from "../pets/petArt";

/**
 * The picker's tabs, in order, with their labels. "emblem" reads as Banner: it is the fortress pennant. Pets
 * (docs/PETS.md) shows once a pet is had (`skinTabs`).
 */
export const SKIN_TABS: readonly { readonly kind: SkinKind; readonly label: string }[] = [
  { kind: "tower", label: "Towers" }, { kind: "fortress", label: "Fortress" }, { kind: "troop", label: "Troops" },
  { kind: "emblem", label: "Banner" }, { kind: "road", label: "Road" },
  // MP-23: the Trophy Hall's two kinds (a second row of tabs: trophyCosmetics.css), then Pets once a pet is had (pets.css)
  { kind: "frame", label: "Title frame" }, { kind: "kill", label: "Kill effect" }, { kind: "pet", label: "Pets" },
];

/** The tabs to show: every kind, Pets only once a pet is had. */
export function skinTabs(petsHad: number): readonly { readonly kind: SkinKind; readonly label: string }[] {
  return SKIN_TABS.filter((t) => t.kind !== "pet" || petsHad > 0);
}

/** A card in the picker: one skin (or the stock look, `id` null) and what the player can do with it. */
export interface SkinCard {
  readonly id: string | null;
  readonly look: SkinLook;
  readonly worn: boolean;
  readonly access: SkinAccess;
  /** The short status under the name ("Worn", "Archer Lv 25", "Ashen Night"…). */
  readonly status: string;
  /** A picture in place of the look's own (a pet's portrait in that coat: docs/PETS.md). */
  readonly art?: string;
  /** Owner 2026-10-21: its gem price in the Shop beside its own route (every skin but the Wyrm's); absent when had. */
  readonly gems?: { readonly price: number; readonly affordable: boolean } | null;
}

/** The event that pays each event emblem (events.json: the dated events' last node). */
const EVENT_NAMES: Readonly<Record<string, string>> = {
  ashen: "Ashen Night", frostfall: "Frostfall Vigil", greenwake: "Greenwake", sunfire: "Sunfire Muster", harvest: "Harvest Moon",
};

/**
 * The cards of one tab: the stock look first, then the catalogue's skins of that kind. `towerId` / `towerName` name
 * the tower a tower skin goes on, or the pet a pet skin goes on.
 */
export function skinCards(k: Cosmetics, kind: SkinKind, towerId: string | null, towerName: string, now: number): SkinCard[] {
  const worn = k.worn(kind, towerId);
  const art = (id: string | null) => (kind === "pet" && towerId ? { art: petPortraitUrl(towerId, id) } : {});
  const stockLook = kind === "pet" && towerId ? lookOf("pet", null, towerId) : STOCK_LOOKS[kind];
  const stock: SkinCard = { id: null, look: stockLook, worn: worn === null, access: { have: true }, status: worn === null ? "Worn" : "Yours", ...art(null) };
  // a Starfall tower skin (M40) is one tower type's own: the archer's picker never shows the frost's; a pet skin its pet's
  return [stock, ...k.list(kind, kind === "tower" || kind === "pet" ? towerId : null).map((s): SkinCard => {
    const access = k.access(s.id, towerId, now);
    const price = access.have ? null : k.gemPrice(s.id, towerId);
    const gems = price === null ? null : { price, affordable: k.wallet.gems >= price };
    return { id: s.id, look: SKIN_LOOKS[s.id] ?? stockLook, worn: worn === s.id, access, status: statusOf(access, worn === s.id, kind, towerName), ...art(s.id), gems };
  })];
}

export function statusOf(a: SkinAccess, worn: boolean, kind: SkinKind, towerName: string): string {
  if (a.have) return worn ? "Worn" : kind === "pet" ? "Caught" : "Yours";
  switch (a.by) {
    case "level": return `${kind === "tower" ? towerName : "Fortress"} Lv ${a.level}`;
    case "event": return EVENT_NAMES[a.emblem] ?? "Event reward";
    case "blackMarket": return a.today ? "Black Market today" : "Black Market";
    case "season": return `Season ${a.season} pass`;
    case "wyrm": return "Starfall Wyrm";
    case "trophies": return "Trophy Hall";
    case "caught": return "Not caught yet";
    default: return "";                                   // the price shows instead
  }
}

/** What the picker's main button does for the selected card. */
export type SkinAction =
  | { readonly kind: "wear" }
  | { readonly kind: "worn" }
  | {
    readonly kind: "buy"; readonly currency: "gems" | "marks"; readonly price: number; readonly affordable: boolean;
    /** A Marks skin's gem price too (owner 2026-10-21): a second BUY. */
    readonly alsoGems?: { readonly price: number; readonly affordable: boolean };
    /** The free route a gem-bought skin keeps ("Or free: Archer reaches Lv 25"), and where it leads, if anywhere. */
    readonly alt?: { readonly text: string; readonly go?: "market" | "trophyHall" };
  }
  | { readonly kind: "market"; readonly price: number }
  /** MP-23: a Trophy Hall exclusive: bought there for Trophies (a link when the Hall is wired, else the line). */
  | { readonly kind: "trophyHall"; readonly price: number }
  | { readonly kind: "locked"; readonly text: string };

export function actionFor(card: SkinCard, kind: SkinKind, towerName: string): SkinAction {
  const a = card.access;
  if (a.have) return card.worn ? { kind: "worn" } : { kind: "wear" };
  const own = routeFor(card, kind, towerName);
  const g = card.gems;
  if (a.by === "marks" && g) return { ...own, alsoGems: g } as SkinAction;
  if (g && own.kind !== "buy") {
    // owner 2026-10-21: every skin but the Wyrm's is for sale for gems; its own route stays, as the free way
    const alt = own.kind === "locked" ? { text: own.text } : own.kind === "market" ? { text: "Or some days at the Black Market", go: "market" as const }
      : own.kind === "trophyHall" ? { text: `Or ${own.price.toLocaleString("en-US")} Trophies in the Trophy Hall`, go: "trophyHall" as const } : undefined;
    return { kind: "buy", currency: "gems", price: g.price, affordable: g.affordable, alt };
  }
  return own;
}

/** The skin's own way to be had (before the gem price, owner 2026-10-21). */
function routeFor(card: SkinCard, kind: SkinKind, towerName: string): SkinAction {
  const a = card.access;
  if (a.have) return card.worn ? { kind: "worn" } : { kind: "wear" };
  switch (a.by) {
    case "gems": case "marks": return { kind: "buy", currency: a.by, price: a.price, affordable: a.affordable };
    case "blackMarket": return a.today ? { kind: "market", price: a.price } : { kind: "locked", text: "Some days at the Black Market" };
    case "level": return { kind: "locked", text: kind === "tower" ? `${towerName} reaches Lv ${a.level}` : `Fortress reaches Lv ${a.level}` };
    case "event": return { kind: "locked", text: `A reward of ${EVENT_NAMES[a.emblem] ?? "an event"}` };
    case "season": return { kind: "locked", text: `War Campaign Premium, tier ${a.tier} of Season ${a.season}: ${a.name}` };
    case "wyrm": return { kind: "locked", text: "Slay the Starfall Wyrm" };
    case "trophies": return { kind: "trophyHall", price: a.price };
    case "caught": return { kind: "locked", text: `Catch a wild ${towerName} wearing it` };
  }
}

/**
 * The picture per look: `art/interface/skins/<id>.png` (144²) and `<id>@2x.png` (288²) with the id's dot as an
 * underscore, `<kind>_stock.png` for the stock looks. Towers, fortresses, troops and roads are in-engine renders of
 * the skinned model (tools/art/skins/thumbs.mjs → render/skins/skinThumb.ts; skins overhaul 2026-10-01), drawn on a
 * transparent ground over the look's two-colour disc; emblems are the painted shields (tools/art/emblems).
 */
const SWATCH_FILE: ReadonlyMap<SkinLook, string> = new Map([
  ...Object.entries(SKIN_LOOKS).filter(([id]) => !(id in PET_KITS)).map(([id, look]) => [look, id.replace(".", "_")] as const),
  ...Object.entries(STOCK_LOOKS).filter(([kind]) => kind !== "pet").map(([kind, look]) => [look, `${kind}_stock`] as const),
]);
/** A pet skin's picture is the pet's portrait in that coat (art/interface/pets, docs/PETS.md), not a skins/ render. */
const PET_ART: ReadonlyMap<SkinLook, string> = new Map(Object.entries(SKIN_LOOKS).flatMap(([id, look]) => (PET_KITS[id] ? [[look, petPortraitUrl(PET_KITS[id].pet, id)] as const] : [])));

/**
 * A skin picture's CSS background (the `.skd` well: a rounded square since the pictures are renders of the model):
 * the render, over a night-blue well lit from below in the skin's own highlight colour (which also stands in until
 * the picture loads).
 */
/** A look's two colours as a lit well (a card's ground; alone, the card of a look with no picture yet). */
export function swatchWell(look: SkinLook): string {
  const hex = (n: number) => `#${n.toString(16).padStart(6, "0")}`;
  return `radial-gradient(ellipse 70% 55% at 50% 72%, ${hex(look.swatch[1])}66, transparent 70%), radial-gradient(circle at 50% 40%, ${hex(look.swatch[0])}, #141b2e 78%)`;
}

export function swatchCss(look: SkinLook, art?: string): string {
  const well = swatchWell(look);
  const portrait = art ?? PET_ART.get(look);
  if (portrait) return `url('${portrait}') center / contain no-repeat, ${well}`;
  const file = SWATCH_FILE.get(look);
  if (!file) return well;
  // single quotes: the value goes into a double-quoted style attribute. The renders have a 2× twin for sharp screens.
  const one = `url('${uiUrl(`skins/${file}.png`)}')`;
  const pic = look.emblem ? one : `image-set(${one} 1x, url('${uiUrl(`skins/${file}@2x.png`)}') 2x)`;   // a banner's shield is one 144² swatch
  return `${pic} center / contain no-repeat, ${well}`;
}

/** The big (@2x, 288²) render of a look, for the picker's stage where no live model turns (the road); null without one. */
export function renderUrl(look: SkinLook): string | null {
  const file = SWATCH_FILE.get(look);
  return file && !look.emblem ? uiUrl(`skins/${file}@2x.png`) : null;
}
